import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { useDashboardContext } from "@/components/dashboard/DashboardContext";
import { useSession } from "@/hooks/auth-hooks";
import { db } from "@/lib/instantdb";

export const Route = createFileRoute("/dashboard/")({
	component: DashboardHomePage,
});

function formatRelativeTime(dateValue: string | null | undefined) {
	if (!dateValue) return "Never";
	const date = new Date(dateValue);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMin = Math.floor(diffMs / 60_000);
	if (diffMin < 1) return "Just now";
	if (diffMin < 60) return `${diffMin}m ago`;
	const diffHr = Math.floor(diffMin / 60);
	if (diffHr < 24) return `${diffHr}h ago`;
	const diffDays = Math.floor(diffHr / 24);
	return `${diffDays}d ago`;
}

function formatClockTime(dateValue: string | null | undefined) {
	if (!dateValue) return "--:--:--";
	const date = new Date(dateValue);
	if (Number.isNaN(date.getTime())) return "--:--:--";
	return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
}

function truncateText(value: string | null | undefined, maxLength = 56) {
	if (!value) return "";
	const normalized = value.trim().replace(/\s+/g, " ");
	if (normalized.length <= maxLength) return normalized;
	return `${normalized.slice(0, maxLength - 1)}…`;
}

type BillingTransaction = {
	amountCents: number;
	createdAt: string;
	description: string | null;
	generationId: string | null;
	id: string;
	provider: string;
	stripeCheckoutSessionId: string | null;
	transactionType: string;
};

type ActivityEntry = {
	color: string;
	text: string;
	timestamp: string;
	url?: string | null;
};

function CopyButton({ text }: { text: string }) {
	const [copied, setCopied] = useState(false);
	const handleCopy = useCallback(() => {
		navigator.clipboard.writeText(text).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	}, [text]);
	return (
		<button
			type="button"
			onClick={handleCopy}
			className="hover:text-[#6B3FF2] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
			style={{ color: "#888888" }}
			aria-label="Copy"
		>
			{copied ? (
				<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "18px", color: "#2b9a66" }}>check</span>
			) : (
				<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "18px" }}>content_copy</span>
			)}
		</button>
	);
}

function JobPreview({ type, outputUrl }: { type: string; outputUrl: string | null }) {
	if (type === "image" && outputUrl) {
		return (
			<div className="w-10 h-10 overflow-hidden shrink-0">
				<img src={outputUrl} alt="" className="w-full h-full object-cover" />
			</div>
		);
	}
	if (type === "video" && outputUrl) {
		return (
			<div className="w-10 h-10 overflow-hidden shrink-0 relative" style={{ backgroundColor: "#000" }}>
				<video src={outputUrl} title="Generated video preview" className="w-full h-full object-cover" muted preload="metadata" />
				<div className="absolute inset-0 flex items-center justify-center bg-black/30">
					<span className="material-symbols-outlined text-white" aria-hidden="true" style={{ fontSize: "14px", fontVariationSettings: "'FILL' 1" }}>play_arrow</span>
				</div>
			</div>
		);
	}
	if (type === "audio") {
		return (
			<div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(43,154,102,0.1)" }}>
				<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "20px", fontVariationSettings: "'FILL' 1", color: "#2b9a66" }}>audio_file</span>
			</div>
		);
	}
	if (type === "video") {
		return (
			<div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(234,40,4,0.1)" }}>
				<span className="material-symbols-outlined text-[#6B3FF2]" aria-hidden="true" style={{ fontSize: "20px" }}>video_library</span>
			</div>
		);
	}
	return (
		<div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(255,107,53,0.08)" }}>
			<span className="material-symbols-outlined text-[#9D7CFF]" aria-hidden="true" style={{ fontSize: "20px" }}>image</span>
		</div>
	);
}

function JobStatusBadge({ status }: { status: string }) {
	if (status === "completed") {
		return (
			<span className="flex items-center gap-1 bg-[rgba(43,154,102,0.1)] border border-[#2b9a66] px-2 py-0.5" style={{ fontFamily: "inherit", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: "bold", color: "#2b9a66" }}>
				<span className="w-1 h-1 rounded-full" style={{ backgroundColor: "#2b9a66" }} />
				Completed
			</span>
		);
	}
	if (status === "processing" || status === "pending") {
		return (
			<span className="flex items-center gap-1 text-[#6B3FF2] bg-[rgba(234,40,4,0.1)] border border-[#6B3FF2] px-2 py-0.5" style={{ fontFamily: "inherit", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: "bold" }}>
				<span className="w-1 h-1 bg-[#6B3FF2] rounded-full animate-ping" />
				{status === "pending" ? "Pending" : "Processing"}
			</span>
		);
	}
	if (status === "failed") {
		return (
			<span className="flex items-center gap-1 text-[#ff6e84] bg-[rgba(255,110,132,0.1)] border border-[#ff6e84] px-2 py-0.5" style={{ fontFamily: "inherit", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: "bold" }}>
				<span className="w-1 h-1 bg-[#ff6e84] rounded-full" />
				Failed
			</span>
		);
	}
	return (
		<span style={{ fontFamily: "inherit", fontSize: "10px", textTransform: "uppercase", fontWeight: "bold", color: "#888888" }}>
			{status}
		</span>
	);
}

function DashboardHomePage() {
	const { user } = useSession();
	const { balanceCents, isBalanceLoading, workspace } = useDashboardContext();

	const { data: userData } = db.useQuery(
		user?.id ? { users: { $: { where: { id: user.id } } } } : null,
	);
	const dbUser = userData?.users?.[0];

	const mcpKeyQuery = useQuery({
		enabled: !!user,
		queryFn: async () => {
			const res = await fetch("/api/mcp/key", { headers: { Accept: "application/json" } });
			if (!res.ok) throw new Error("Failed to fetch MCP key");
			return res.json() as Promise<{
				key: {
					configured: boolean;
					id?: string | null;
					keyPrefix: string | null;
					lastUsedAt: string | null;
					name: string | null;
					updatedAt: string | null;
				};
				keys: Array<{
					configured: boolean;
					id?: string | null;
					keyPrefix: string | null;
					lastUsedAt: string | null;
					name: string | null;
					updatedAt: string | null;
				}>;
			}>;
		},
		queryKey: ["dashboard", "mcp-key"],
		refetchInterval: 30_000,
	});
	const mcpKey = mcpKeyQuery.data?.key;
	const mcpKeys = mcpKeyQuery.data?.keys ?? [];

	const recentGenerationsQuery = useQuery({
		enabled: !!user,
		queryFn: async () => {
			const response = await fetch("/api/mcp/generations?limit=6", {
				headers: { Accept: "application/json" },
			});
			if (!response.ok) throw new Error("Failed to fetch recent generations");
			return response.json() as Promise<{
				generations: Array<{
					createdAt: string;
					generationType: string;
					id: string;
					outputUrl: string | null;
					prompt: string | null;
					status: string;
					title: string | null;
					updatedAt: string;
				}>;
			}>;
		},
		queryKey: ["dashboard", "recent-generations"],
		refetchInterval: 15_000,
	});
	const recentGenerations = recentGenerationsQuery.data?.generations ?? [];
	const billingHistoryQuery = useQuery({
		enabled: !!user,
		queryFn: async () => {
			const response = await fetch("/api/mcp/billing-history?limit=8", {
				headers: { Accept: "application/json" },
			});
			if (!response.ok) throw new Error("Failed to fetch billing history");
			return response.json() as Promise<{ transactions: BillingTransaction[] }>;
		},
		queryKey: ["dashboard", "billing-history"],
		refetchInterval: 15_000,
	});
	const billingTransactions = billingHistoryQuery.data?.transactions ?? [];

	const balanceDollars = (balanceCents / 100).toFixed(2);
	const lastSeen = dbUser?.agentLastSeenAt ? String(dbUser.agentLastSeenAt) : null;
	const billedGenerationIds = new Set(
		billingTransactions
			.filter(
				(transaction) =>
					transaction.transactionType === "debit" && Boolean(transaction.generationId),
			)
			.map((transaction) => transaction.generationId!),
	);
	const mcpActivity: ActivityEntry[] = [
		...(mcpKey?.lastUsedAt
			? [{
				timestamp: mcpKey.lastUsedAt,
				text: `AUTH_SUCCESS: ${mcpKey.name || "Primary MCP key"} authenticated successfully.`,
				color: "#6B3FF2",
			}]
			: []),
		...(lastSeen
			? [{
				timestamp: lastSeen,
				text: `CLIENT_ACTIVITY: MCP client last seen ${formatRelativeTime(lastSeen)}.`,
				color: "#888888",
			}]
			: []),
		...billingTransactions
			.filter((transaction) => transaction.transactionType === "debit")
			.map((transaction) => ({
				timestamp: transaction.createdAt,
				text: `API_CALL: ${transaction.description || "Usage charge"}.`,
				color: "#2b9a66",
			})),
		...recentGenerations
			.filter((generation) => !billedGenerationIds.has(generation.id))
			.map((generation) => {
			const label = truncateText(generation.title || generation.prompt, 64) || `${generation.generationType} generation`;
			if (generation.status === "complete" || generation.status === "completed") {
				return {
					timestamp: String(generation.updatedAt),
					text: `TOOL_CALL_COMPLETED: ${generation.generationType} job finished for "${label}".`,
					color: "#2b9a66",
					url: generation.outputUrl,
				};
			}
			if (generation.status === "failed") {
				return {
					timestamp: String(generation.updatedAt),
					text: `TOOL_CALL_FAILED: ${generation.generationType} job failed for "${label}".`,
					color: "#ff6e84",
				};
			}
			return {
				timestamp: String(generation.createdAt),
				text: `TOOL_CALL_STARTED: ${generation.generationType} job queued for "${label}".`,
				color: "#888888",
			};
		}),
	]
		.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
		.slice(0, 8);
	const latestActivityAt = mcpActivity[0]?.timestamp ?? null;
	const isActivityLive =
		latestActivityAt != null &&
		Date.now() - new Date(latestActivityAt).getTime() < 5 * 60_000;

	return (
		<DashboardLayout>
			{/* Header */}
				<header className="flex flex-col sm:flex-row justify-between items-start gap-6 mb-8 md:mb-12">
					<div>
						<p
							className="text-[11px] font-bold uppercase tracking-[0.1em] mb-2"
							style={{ fontFamily: "inherit", color: "#6B3FF2" }}
						>
							Dashboard
						</p>
						<h1
							className="text-4xl md:text-5xl font-semibold tracking-tighter mb-2"
							style={{ fontFamily: "'Geist', -apple-system, sans-serif" }}
						>
							Developer Dashboard
						</h1>
						<p
							className="text-xs uppercase tracking-wide font-bold"
							style={{ fontFamily: "inherit", color: "#888888" }}
						>
							Environment for {workspace?.name ?? "-"}
						</p>
					</div>
					{/* Balance card */}
					<div
						className="flex items-center gap-4 p-4 shrink-0"
						style={{ backgroundColor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #6B3FF2" }}
					>
						<div className="flex flex-col items-end">
							<span
								className="text-[10px] uppercase tracking-[0.1em] font-bold"
								style={{ fontFamily: "inherit", color: "#6B3FF2" }}
							>
								Balance
							</span>
							{isBalanceLoading ? (
								<span className="material-symbols-outlined animate-spin text-[#6B3FF2]" aria-hidden="true" style={{ fontSize: "16px" }}>progress_activity</span>
							) : (
								<span
									className="text-2xl font-semibold text-[#6B3FF2]"
									style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace" }}
								>
									${balanceDollars}{" "}
									<span className="text-xs" style={{ color: "#6B3FF2" }}>CREDITS</span>
								</span>
							)}
						</div>
						<div className="h-10 w-px" style={{ backgroundColor: "rgba(255,255,255,0.08)" }} />
						<Link
							to="/dashboard/billing"
							search={{ checkout: undefined }}
							aria-label="Add funds"
							className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
						>
							<span
								className="material-symbols-outlined hover:text-[#6B3FF2] transition-colors cursor-pointer"
								aria-hidden="true"
								style={{ fontSize: "24px", color: "#888888" }}
							>
								add_circle
							</span>
						</Link>
					</div>
				</header>

				{/* 12-col grid */}
				<div className="grid grid-cols-12 gap-8">
					{/* API Keys Section, 8 cols */}
					<section
						aria-label="API access keys"
						className="col-span-12 lg:col-span-8 p-8 relative overflow-hidden"
						style={{ backgroundColor: "#111111", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #6B3FF2" }}
					>
						{/* Watermark icon */}
						<div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none" aria-hidden="true">
							<span className="material-symbols-outlined text-[#6B3FF2]" style={{ fontSize: "9rem" }}>key</span>
						</div>

						<div
							className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-8 pb-6"
							style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
						>
							<div>
								<p
									className="text-[11px] font-bold uppercase tracking-[0.1em] mb-1"
									style={{ fontFamily: "inherit", color: "#6B3FF2" }}
								>
									Authentication
								</p>
								<h2
									className="text-xl font-semibold tracking-tight"
									style={{ fontFamily: "'Geist', -apple-system, sans-serif" }}
								>
									API Access Keys
								</h2>
								<p className="text-sm mt-1" style={{ color: "#888888" }}>
									Your MCP key authenticates all tool calls from your agent.
								</p>
							</div>
							<Link to="/dashboard/api-keys">
								<button
									type="button"
									className="text-xs font-bold px-6 py-3 transition-all hover:opacity-90 active:scale-95 flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
									style={{
										fontFamily: "inherit",
										backgroundColor: "#6B3FF2",
										color: "#ffffff",
										letterSpacing: "0.1em",
										textTransform: "uppercase",
									}}
								>
									<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px", fontVariationSettings: "'FILL' 1" }}>add</span>
									Create New Key
								</button>
							</Link>
						</div>

						<div className="overflow-x-auto">
							<table className="w-full text-left" style={{ borderCollapse: "separate", borderSpacing: "0 12px" }}>
								<thead>
									<tr
										className="text-[10px] uppercase tracking-wide font-bold"
										style={{ fontFamily: "inherit", color: "#888888" }}
									>
										<th scope="col" className="pb-2 font-bold px-4">Key Name</th>
										<th scope="col" className="pb-2 font-bold px-4">Key Hash</th>
										<th scope="col" className="pb-2 font-normal px-4 hidden sm:table-cell">Last Used</th>
										<th scope="col" className="pb-2 font-normal px-4 hidden sm:table-cell">Status</th>
										<th scope="col" className="pb-2 font-normal px-4 text-right">Actions</th>
									</tr>
								</thead>
								<tbody style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", fontSize: "14px" }}>
									{mcpKeyQuery.isLoading ? (
										<tr>
											<td colSpan={5} className="px-4 py-6">
												<div className="flex items-center gap-2" style={{ color: "#888888" }}>
													<span className="material-symbols-outlined animate-spin" aria-hidden="true" style={{ fontSize: "14px" }}>progress_activity</span>
													<span className="text-xs">Loading…</span>
												</div>
											</td>
										</tr>
									) : mcpKeys.length > 0 ? (
										mcpKeys.slice(0, 3).map((entry) => (
											<tr
												key={entry.id ?? entry.keyPrefix ?? entry.name ?? "mcp-key"}
												className="transition-colors"
												style={{ backgroundColor: "rgba(255,255,255,0.04)" }}
												onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#262626")}
												onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#201f1f")}
											>
												<td className="py-4 px-4 font-bold text-white" style={{ fontFamily: "'Geist', -apple-system, sans-serif" }}>
													{entry.name || "MCP Key"}
												</td>
												<td className="py-4 px-4" style={{ color: "#888888" }}>
													{entry.keyPrefix ? `${entry.keyPrefix}••••••••` : "sk_af_••••••••"}
												</td>
												<td className="py-4 px-4 text-xs hidden sm:table-cell" style={{ color: "#888888" }}>
													{formatRelativeTime(entry.lastUsedAt)}
												</td>
												<td className="py-4 px-4 hidden sm:table-cell">
													<span
														className="flex items-center gap-1.5 text-[#6B3FF2] text-[10px] uppercase tracking-[0.1em] bg-[rgba(234,40,4,0.1)] border border-[#6B3FF2] px-2 py-0.5"
														style={{ fontFamily: "inherit" }}
													>
														<span className="w-1.5 h-1.5 bg-[#6B3FF2] rounded-full animate-pulse" />
														Active
													</span>
												</td>
												<td className="py-4 px-4 text-right">
													<CopyButton text={entry.keyPrefix || ""} />
												</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan={5} className="px-4 py-8 text-center">
												<p className="text-sm mb-3" style={{ color: "#888888" }}>No API key configured yet.</p>
												<Link to="/dashboard/api-keys">
													<button
														type="button"
														className="text-xs px-4 py-2 transition-colors hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
														style={{
															fontFamily: "inherit",
															border: "1px solid #6B3FF2",
															color: "#6B3FF2",
															textTransform: "uppercase",
															letterSpacing: "0.1em",
														}}
													>
														Create API Key
													</button>
												</Link>
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</section>

					{/* Right column, 4 cols */}
					<section aria-label="Statistics and recent jobs" className="col-span-12 lg:col-span-4 flex flex-col gap-8">
						{/* Stats mini cards */}
						<div className="grid grid-cols-2 gap-4">
							<div
								className="p-6"
								style={{ backgroundColor: "#111111", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #2b9a66" }}
							>
								<span
									className="text-[10px] uppercase tracking-[0.1em] font-bold"
									style={{ fontFamily: "inherit", color: "#2b9a66" }}
								>
									Uptime
								</span>
								<div
									className="text-2xl font-semibold mt-1"
									style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", color: "#2b9a66" }}
								>
									99.9%
								</div>
							</div>
							<div
								className="p-6"
								style={{ backgroundColor: "#111111", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #6B3FF2" }}
							>
								<span
									className="text-[10px] uppercase tracking-[0.1em] font-bold"
									style={{ fontFamily: "inherit", color: "#6B3FF2" }}
								>
									Latency
								</span>
								<div
									className="text-2xl font-semibold mt-1 text-[#6B3FF2]"
									style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace" }}
								>
									24ms
								</div>
							</div>
						</div>

						{/* Recent Jobs */}
						<div
							className="flex-1 p-6 relative overflow-hidden"
							style={{ backgroundColor: "#111111", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #6B3FF2" }}
						>
							<div className="flex items-center justify-between mb-6">
								<div>
									<p
										className="text-[11px] font-bold uppercase tracking-[0.1em] mb-0.5"
										style={{ fontFamily: "inherit", color: "#6B3FF2" }}
									>
										Archive
									</p>
									<h3
										className="font-semibold text-lg"
										style={{ fontFamily: "'Geist', -apple-system, sans-serif" }}
									>
										Recent Jobs
									</h3>
								</div>
								<Link
									to="/dashboard/generations"
									className="text-xs font-bold uppercase tracking-wide transition-colors hover:text-[#6B3FF2] flex items-center gap-1"
									style={{ fontFamily: "inherit", color: "#888888" }}
								>
									View All
									<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "12px" }}>arrow_forward</span>
								</Link>
							</div>

							{recentGenerationsQuery.isLoading ? (
								<div className="flex items-center gap-2 text-xs" style={{ color: "#888888" }}>
									<span className="material-symbols-outlined animate-spin" aria-hidden="true" style={{ fontSize: "14px" }}>progress_activity</span>
									Loading…
								</div>
							) : recentGenerations.length === 0 ? (
								<p className="text-sm" style={{ color: "#888888" }}>
									No generations yet. Ask your agent to generate an image, video, or audio clip.
								</p>
							) : (
								<div className="space-y-3">
									{recentGenerations.map((gen) => (
										<div
											key={gen.id}
											className="flex items-center gap-4 p-4 rounded-none group transition-colors cursor-default"
											style={{ backgroundColor: "#000000" }}
											onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#201f1f")}
											onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#000000")}
										>
											<JobPreview type={gen.generationType} outputUrl={gen.outputUrl} />
											<div className="flex-1 overflow-hidden">
												<p
													className="text-sm truncate"
													style={{ fontFamily: "inherit" }}
												>
													{gen.prompt?.trim() || "Untitled generation"}
												</p>
												<div className="flex items-center gap-2 mt-0.5">
													<span
														className="text-[10px]"
														style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", color: "#888888" }}
													>
														{gen.generationType}
													</span>
													<JobStatusBadge status={gen.status} />
												</div>
											</div>
											{gen.outputUrl ? (
												<a
													href={gen.outputUrl}
													target="_blank"
													rel="noreferrer"
													aria-label={`Open ${gen.prompt?.trim() || gen.generationType} result (opens in new tab)`}
													className="group-hover:text-[#6B3FF2] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
												style={{ color: "#888888" }}
												>
													<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "18px" }}>open_in_new</span>
												</a>
											) : gen.status === "processing" || gen.status === "pending" ? (
												<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "18px", color: "#888888" }}>hourglass_empty</span>
											) : (
												<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "18px", color: "#888888" }}>refresh</span>
											)}
										</div>
									))}
								</div>
							)}
						</div>
					</section>
				</div>

				{/* MCP Activity */}
				<section aria-label="MCP activity log" className="mt-12">
					<div
						className="p-6 relative"
						style={{
							backgroundColor: "var(--km-surface)",
							border: "1px solid rgba(255,255,255,0.08)",
							borderTop: "3px solid #6B3FF2",
						}}
					>
						<div
							className="flex items-center gap-4 mb-4 pb-4"
							style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
						>
							<div className="flex gap-1.5" aria-hidden="true">
								<div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#ff5f57" }} />
								<div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#febc2e" }} />
								<div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#28c840" }} />
							</div>
							<span
								className="text-xs"
								style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", color: "var(--km-on-surface-muted)" }}
							>
								system.api.mcp.activity, recent_events (local time)
							</span>
							{isActivityLive && (
								<div className="ml-auto flex items-center gap-1.5" aria-label="Live updates active">
									<span className="w-1.5 h-1.5 rounded-full animate-pulse" aria-hidden="true" style={{ backgroundColor: "#2b9a66" }} />
									<span
										className="text-[10px] uppercase tracking-[0.1em] font-bold"
										aria-hidden="true"
										style={{ fontFamily: "inherit", color: "#2b9a66" }}
									>
										Live
									</span>
								</div>
							)}
						</div>
						<div
							className="font-mono text-xs space-y-2 leading-relaxed h-32 overflow-y-auto"
							role="log"
							aria-live="polite"
							aria-atomic="false"
							aria-label="MCP activity log"
							style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace" }}
						>
							{mcpActivity.length > 0 ? (
								mcpActivity.map((entry) => (
									<p key={`${entry.timestamp}-${entry.text}`} style={{ color: entry.color }}>
										<span style={{ color: "#28c840" }}>[{formatClockTime(entry.timestamp)}]</span>{" "}{entry.text}
										{entry.url ? (
											<>
												{" "}
												<a
													href={entry.url}
													target="_blank"
													rel="noreferrer"
													aria-label="open result (opens in new tab)"
													className="underline underline-offset-2 transition-colors hover:text-[#6B3FF2]"
													style={{ color: "inherit" }}
												>
													open result
												</a>
											</>
										) : null}
									</p>
								))
							) : (
								<p style={{ color: "var(--km-on-surface-muted)" }}>
									[{formatClockTime(null)}] No activity yet. Connect your agent and run your first tool call to see events here.
								</p>
							)}
						</div>
					</div>
				</section>
		</DashboardLayout>
	);
}
